import {useCallback, useRef} from 'react';

/**
 * Returns a stable callback reference that always delegates to the latest
 * version of `func` without needing it listed as a dependency.
 *
 * Solves the classic "stale closure" problem: the returned function has a
 * stable identity (never changes between renders, safe to pass to `useEffect`
 * dependency arrays or memoized children), while internally it forwards all
 * calls to the most recent `func` stored in a ref.
 *
 * This is equivalent to the `useEvent` RFC pattern and is the recommended
 * alternative to `useCallback` when the callback depends on frequently
 * changing values that would otherwise force expensive downstream re-renders.
 *
 * @param func - The function to wrap. Updated on every render via a ref, so
 *   the latest closure is always invoked.
 * @returns A stable function with the same signature as `func` whose identity
 *   never changes across re-renders.
 *
 * @example
 * ```tsx
 * const onScroll = useCallbackWithRef((e: Event) => {
 *   // can safely reference props/state without deps array
 *   setScrollTop((e.target as Element).scrollTop);
 * });
 *
 * useEffect(() => {
 *   window.addEventListener('scroll', onScroll);
 *   return () => window.removeEventListener('scroll', onScroll);
 * }, [onScroll]); // onScroll is stable — effect runs only once
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useCallbackWithRef = <F extends (...args: any[]) => any>(func: F): F => {
    const funcRef = useRef(func);
    funcRef.current = func;


    return useCallback(((...args: Parameters<F>) => funcRef.current(...args)) as F, []);
};
